import React, { createContext, useState, ReactNode, useEffect } from "react";
import { Snackbar, Box, Typography, IconButton } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faCheck,
  faTimes,
  faExclamationTriangle, 
  faInfoCircle,
  faCheckCircle,
  faTimesCircle
} from "@fortawesome/free-solid-svg-icons";

interface SnackbarContextProps {
  showMessage: (
    title: string,
    message: string,
    severity: "success" | "error" | "warning" | "info",
  ) => void;
}

export const SnackbarContext = createContext<SnackbarContextProps | undefined>(
  undefined,
);

let snackbarContextValue: SnackbarContextProps;

export const SnackbarProvider = ({ children }: { children: ReactNode }) => {
  const [snackbar, setSnackbar] = useState<{
    title: string;
    message: string[];
    severity: "success" | "error" | "warning" | "info";
    open: boolean;
  }>({
    title: "",
    message: [],
    severity: "info",
    open: false,
  });

  const [progress, setProgress] = useState(100);
  const DURATION = 4000;

  const showMessage = (
    title: string,
    message: string,
    severity: "success" | "error" | "warning" | "info",
  ) => {
    const phrases = message.split(".").filter((phrase) => phrase.trim() !== "");
    setSnackbar({ title, message: phrases, severity, open: true });
    setProgress(100);
  };

  const handleClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    if (snackbar.open) {
      const timer = setInterval(() => {
        setProgress((prev) => Math.max(0, prev - (100 / (DURATION / 10))));
      }, 10);
      return () => clearInterval(timer);
    }
  }, [snackbar.open]);

  snackbarContextValue = { showMessage };

  const getSeverityConfig = (severity: "success" | "error" | "warning" | "info") => {
    switch (severity) {
      case 'success':
        return {
          icon: faCheck,
          color: '#10B981',
          bgColor: 'bg-emerald-500/10',
          gradient: 'from-emerald-400 to-emerald-600',
          borderColor: 'border-emerald-500/20',
          text: 'text-emerald-900',
          lightColor: 'rgba(16, 185, 129, 0.2)'
        };
      case 'error':
        return {
          icon: faTimes,
          color: '#F43F5E',
          bgColor: 'bg-rose-500/10',
          gradient: 'from-rose-400 to-rose-600',
          borderColor: 'border-rose-500/20',
          text: 'text-rose-900',
          lightColor: 'rgba(244, 63, 94, 0.2)'
        };
      case 'warning':
        return {
          icon: faExclamationTriangle,
          color: '#F59E0B',
          bgColor: 'bg-amber-500/10',
          gradient: 'from-amber-400 to-amber-600',
          borderColor: 'border-amber-500/20',
          text: 'text-amber-900',
          lightColor: 'rgba(245, 158, 11, 0.2)'
        };
      default:
        return {
          icon: faInfoCircle,
          color: '#3B82F6',
          bgColor: 'bg-blue-500/10',
          gradient: 'from-blue-400 to-blue-600',
          borderColor: 'border-blue-500/20',
          text: 'text-blue-900',
          lightColor: 'rgba(59, 130, 246, 0.2)'
        };
    }
  };

  const config = getSeverityConfig(snackbar.severity);

  return (
    <SnackbarContext.Provider value={snackbarContextValue}>
      {children}
      <Snackbar
        open={snackbar.open}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={DURATION}
        onClose={handleClose}
        disableWindowBlurListener
        sx={{ 
          mt: 2, 
          mr: 2,
          '& .MuiPaper-root': { bgcolor: 'transparent', boxShadow: 'none' } 
        }}
      >
        <Box
          sx={{
            minWidth: 320,
            maxWidth: 450,
            borderRadius: '1.5rem',
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden',
            bgcolor: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(20px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            boxShadow: `
              0 10px 30px -5px rgba(0, 0, 0, 0.1),
              0 0 0 1px rgba(0, 0, 0, 0.05),
              inset 0 0 20px 0 ${config.lightColor}
            `,
            animation: 'toast-in 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <div className="p-5 flex items-start gap-5">
             <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg relative group overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-br ${config.gradient} opacity-90`}></div>
                <FontAwesomeIcon icon={config.icon} className="text-white text-lg relative z-10" />
                <div className="absolute inset-0 bg-white/20 blur-xl scale-150"></div>
             </div>

             <div className="flex-1 pt-1">
                <div className="flex items-center justify-between mb-1">
                  <h4 className={`text-[10px] font-black uppercase tracking-[0.2em] ${config.text} opacity-70`}>
                     {snackbar.severity}
                  </h4>
                  <IconButton 
                    size="small"
                    onClick={handleClose}
                    className="mt-[-4px] mr-[-4px] text-gray-400 hover:text-gray-600"
                  >
                    <FontAwesomeIcon icon={faTimes} className="text-xs" />
                  </IconButton>
                </div>
                <Typography className="text-title font-black text-[13px] uppercase tracking-tight leading-none mb-1">
                  {snackbar.title}
                </Typography>
                <div className="space-y-0.5">
                  {snackbar.message.map((phrase, index) => (
                    <p key={index} className="text-[#1A1D1F]/70 text-[11px] font-bold leading-snug">
                      {phrase.trim()}{phrase.endsWith('!') ? '' : '.'}
                    </p>
                  ))}
                </div>
             </div>
          </div>

          <div className="h-1.5 w-full bg-black/5 overflow-hidden mt-auto">
              <div 
                className={`h-full bg-gradient-to-r ${config.gradient}`}
                style={{ width: `${progress}%`, transition: 'width 10ms linear' }}
              />
          </div>

          <style>{`
            @keyframes toast-in {
              0% { transform: translateX(100%) scale(0.9); opacity: 0; }
              100% { transform: translateX(0) scale(1); opacity: 1; }
            }
          `}</style>
        </Box>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export const getSnackbarContext = () => snackbarContextValue;
